import { ChevronLeft } from "lucide-react";
import type { FormEvent, ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { useMemberProfile } from "../MemberProfileContext";

export function FormShell({
    title,
    subtitle,
    showBack,
    children,
    footer,
    onClose,
    onSave,
    saveLabel = "Save",
    saveDisabled,
}: {
    title: string;
    subtitle?: string;
    showBack?: boolean;
    children: ReactNode;
    footer?: ReactNode;
    onClose: () => void;
    onSave?: (event: FormEvent<HTMLFormElement>) => void;
    saveLabel?: string;
    saveDisabled?: boolean;
}) {
    const { saving } = useMemberProfile();
    const disableSave = saveDisabled || saving;

    const headerAndBody = (
        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-3 pt-5 pb-4">
            <div className="flex items-start gap-2">
                {showBack && (
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        aria-label="Back"
                        onClick={onClose}
                    >
                        <ChevronLeft />
                    </Button>
                )}
                <div className="min-w-0 flex-1">
                    <h2 className="heading-4! text-zinc-900">{title}</h2>
                    {subtitle && (
                        <p className="text-xs text-zinc-500 leading-4">{subtitle}</p>
                    )}
                </div>
            </div>
            {children}
        </div>
    );

    const actions = (
        <div className="flex w-full shrink-0 justify-end gap-2 border-zinc-200 border-t px-3 py-3">
            {footer ?? (
                <>
                    <Button type="button" variant="outline" onClick={onClose}>
                        {onSave ? "Cancel" : "Close"}
                    </Button>
                    {onSave && (
                        <Button type="submit" disabled={disableSave}>
                            {saveLabel}
                        </Button>
                    )}
                </>
            )}
        </div>
    );

    if (!onSave) {
        return (
            <div className="absolute inset-0 z-10 flex flex-col bg-white">
                {headerAndBody}
                {actions}
            </div>
        );
    }

    return (
        <form
            className="absolute inset-0 z-10 flex flex-col bg-white"
            onSubmit={(event) => {
                event.preventDefault();
                onSave(event);
            }}
        >
            {headerAndBody}
            {actions}
        </form>
    );
}
